import { useCallback, useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import SEO from 'components/Seo/Seo';
import { useGetAccessToSegmentQuery } from '@store/slices/profile-api';
import {
  Chat,
  ChatSidebar,
  STORAGE_KEY,
} from 'components/AiInsights/components/ChatSidebar';
import { ChatView } from 'components/AiInsights/components/ChatView';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';

export const Route = createFileRoute('/_authenticated/ai-insights/')({
  component: AIInsights,
});

function AIInsights() {
  const navigate = useNavigate();
  const { data: hasAccess, isLoading } =
    useGetAccessToSegmentQuery('BETA_AI_FEATURES');
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [sidebarKey, setSidebarKey] = useState(0);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !hasAccess) {
      navigate({ to: '/' });
    }
  }, [hasAccess, isLoading, navigate]);

  // Hide Zendesk widget on this page
  useEffect(() => {
    window.zE?.('messenger', 'hide');
    return () => {
      window.zE?.('messenger', 'show');
    };
  }, []);

  const handleNewChat = useCallback(() => {
    setActiveChatId(null);
  }, []);

  const handleSelectChat = useCallback((chat: Chat) => {
    setActiveChatId(chat.id);
  }, []);

  const handleDeleteChat = useCallback(
    (chatId: string) => {
      if (activeChatId === chatId) {
        setActiveChatId(null);
      }
    },
    [activeChatId],
  );

  const handleFirstMessage = useCallback((question: string) => {
    const newChat: Chat = {
      id: crypto.randomUUID(),
      question,
      createdAt: Date.now(),
    };
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const chats: Chat[] = stored ? JSON.parse(stored) : [];
      localStorage.setItem(STORAGE_KEY, JSON.stringify([newChat, ...chats]));
    } catch {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([newChat]));
    }
    setActiveChatId(newChat.id);
    setSidebarKey(prev => prev + 1);
    return newChat.id;
  }, []);

  if (isLoading || !hasAccess) return null;

  return (
    <>
      <SEO
        title={
          'AI Insights - Analyze your financial data with AI-powered insights'
        }
        description={
          'Get actionable insights from your financial data using AI-powered analysis.'
        }
        keywords={'AI, financial insights, data analysis, machine learning'}
        page={'AI Insights'}
      />
      <div className="flex h-full">
        <div className="hidden lg:flex flex-col w-64 bg-[#0F1729] h-full shrink-0 border-r border-neutral-700">
          <ChatSidebar
            key={sidebarKey}
            activeChatId={activeChatId}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
            onNewChat={handleNewChat}
          />
        </div>
        <div className="flex-1 h-full">
          <ChatView
            chatId={activeChatId}
            onFirstMessage={handleFirstMessage}
            onOpenHistory={() => setIsHistoryOpen(true)}
            onNewChat={handleNewChat}
          />
        </div>
      </div>
      <DialogOrBottomSheet
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        backgroundColor="#0F1729"
        blurEnabled
        detent="full-height">
        <ChatSidebar
          key={sidebarKey}
          activeChatId={activeChatId}
                    onNewChat={() => {
            handleNewChat();
            setIsHistoryOpen(false);
          }}
          onSelectChat={chat => {
            handleSelectChat(chat);
            setIsHistoryOpen(false);
          }}
          onDeleteChat={handleDeleteChat}
        />
      </DialogOrBottomSheet>
    </>
  );
}
